import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgaveVendor } from 'API';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAgaveSourceSystem } from 'app/hooks/useAgaveSourceSystem';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { getErpPostingAccount } from 'app/services/erp-connection.service';

const SPECTRUM_ERP = 'spectrum';

export interface SpectrumPostingVendorLock {
  /** True when the transaction vendor dropdown should be pre-filled + locked to the
   *  Connections posting vendor: Spectrum + direct connect + a posting vendor is set. */
  locked: boolean;
  /** The Connections posting vendor to display (minimal AgaveVendor: id + name). */
  vendor: AgaveVendor | null;
  /** Explanation copy for the locked control (desktop tooltip / mobile + bulk helper). */
  tooltip: string;
}

/**
 * SEE-835 — Spectrum is a single-vendor ERP: a direct Spectrum AP invoice always posts
 * against the Connections "posting vendor", and the per-transaction vendor is dropped.
 * To avoid a misleading mismatch (coding vendor B but posting to A), lock the transaction
 * vendor dropdown to that posting vendor. Display-only: we never overwrite stored coding.
 *
 * Excludes Vista, where the transaction vendor is the real line `Supplier` (the merchant)
 * and must stay editable. Callers should additionally gate on the transaction being
 * unposted — posted rows render their historical coding read-only.
 */
export function useSpectrumPostingVendorLock(): SpectrumPostingVendorLock {
  const { t } = useTranslation();
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { sourceSystemIn } = useAgaveSourceSystem();
  const paidolId = selectedPaidol?.id;
  const tooltip = t(
    'transactions.spectrumPostingVendorLocked',
    'Spectrum posts every transaction against the posting vendor set on the Connections page. Change it there.'
  );
  // Match the codebase's direct-connect convention (erpSpeedchain && erpConnectionId,
  // see useAccountType / RequireAuth): a paidol mid-setup or post-disconnect has
  // erpSpeedchain=true but a cleared erpConnectionId and is NOT actively connected.
  const isSpectrumDirect =
    sourceSystemIn(AgaveSourceSystem.SPECTRUM) &&
    Boolean(selectedPaidol?.erpSpeedchain && selectedPaidol?.erpConnectionId);

  const [vendor, setVendor] = useState<AgaveVendor | null>(null);

  useEffect(() => {
    if (!isSpectrumDirect || !paidolId) {
      setVendor(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const account = await getErpPostingAccount(paidolId, SPECTRUM_ERP);
        if (cancelled) return;
        setVendor(
          account?.accountId
            ? {
                __typename: 'AgaveVendor',
                id: account.accountId,
                name: account.accountName ?? account.accountId,
              }
            : null
        );
      } catch {
        // Non-fatal — treat as no override (dropdown stays editable).
        if (!cancelled) setVendor(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSpectrumDirect, paidolId]);

  if (!isSpectrumDirect || !vendor) return { locked: false, vendor: null, tooltip };
  return { locked: true, vendor, tooltip };
}
